import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../auth/useAuth";
import {
  canBuyDeploymentCapacityPack,
  getPlanLabel,
  isPaidPlan,
  normalizePlanCode,
} from "../components/billing/plans";

function safeApiError(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function clearCheckoutQueryParams() {
  const url = new URL(window.location.href);

  if (!url.searchParams.has("checkout") && !url.searchParams.has("_ptxn")) {
    return;
  }

  url.searchParams.delete("checkout");
  url.searchParams.delete("_ptxn");

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", nextUrl || "/upgrade");
}

export default function Upgrade() {
  const { isAuthed } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const checkoutState = params.get("checkout");

  const [accountUsage, setAccountUsage] = useState(null);
  const [accountUsageLoading, setAccountUsageLoading] = useState(false);
  const [accountUsageError, setAccountUsageError] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [acceptedBillingTerms, setAcceptedBillingTerms] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);

  const loadAccountUsage = useCallback(async () => {
    if (!isAuthed) {
      setAccountUsage(null);
      setAccountUsageError("");
      return;
    }

    setAccountUsageLoading(true);
    setAccountUsageError("");

    try {
      const res = await api.get("/account/usage", {
        headers: { "Cache-Control": "no-store" },
      });
      setAccountUsage(res.data || null);
    } catch (err) {
      setAccountUsage(null);
      setAccountUsageError(safeApiError(err, "Failed to load account usage."));
    } finally {
      setAccountUsageLoading(false);
    }
  }, [isAuthed]);

  const startPlanCheckout = useCallback(async (plan) => {
    if (!isAuthed) {
      setCheckoutError("Sign in before upgrading.");
      return;
    }

    const normalizedPlan = normalizePlanCode(plan);

    if (normalizedPlan !== "starter" && normalizedPlan !== "pro") {
      setCheckoutError("Choose a valid paid plan.");
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError("");

    try {
      const res = await api.post("/billing/checkout-session", {
        plan: normalizedPlan,
      });

      const transactionId = String(res?.data?.transactionId || "").trim();
      const url = String(res?.data?.url || "").trim();

      if (transactionId) {
        window.location.href = `/upgrade?_ptxn=${encodeURIComponent(
          transactionId
        )}&plan=${encodeURIComponent(normalizedPlan)}`;
        return;
      }

      if (!url) {
        throw new Error("Missing checkout URL.");
      }

      window.location.href = url;
    } catch (err) {
      setCheckoutError(safeApiError(err, "Failed to start checkout."));
    } finally {
      setCheckoutLoading(false);
    }
  }, [isAuthed]);

  const handlePlanCheckout = useCallback((plan) => {
    if (!isAuthed) {
      setCheckoutError("Sign in before upgrading.");
      return;
    }

    const normalizedPlan = normalizePlanCode(plan);

    if (normalizedPlan !== "starter" && normalizedPlan !== "pro") {
      setCheckoutError("Choose a valid paid plan.");
      return;
    }

    if (!acceptedBillingTerms) {
      setPendingCheckout({
        type: "plan",
        plan: normalizedPlan,
      });
      setTermsModalOpen(true);
      setCheckoutError("");
      return;
    }

    startPlanCheckout(normalizedPlan);
  }, [isAuthed, acceptedBillingTerms, startPlanCheckout]);



  useEffect(() => {
    loadAccountUsage();
  }, [loadAccountUsage]);

  useEffect(() => {
    if (
      checkoutState !== "success" &&
      checkoutState !== "starter_success" &&
      checkoutState !== "pro_success" &&
      checkoutState !== "extra_success" &&
      checkoutState !== "cancel"
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      clearCheckoutQueryParams();
    }, 3500);

    return () => window.clearTimeout(timer);
  }, [checkoutState]);

  const planSummary = useMemo(() => {
    const usage = accountUsage?.usage || {};
    const limits = accountUsage?.limits || {};
    const plan = normalizePlanCode(accountUsage?.plan || "free");
    const isUnlimited = Boolean(limits?.unlimited) || Number(limits?.deployments) < 0;

    const deploymentsUsed = Number(usage?.deploymentsUsed || 0);
    const deploymentsLimit = isUnlimited
      ? "Unlimited"
      : Number(limits?.deployments || 0);

    const forkSimulationsUsed = Number(usage?.forkSimulationsUsed || 0);
    const forkSimulationsLimit = isUnlimited
      ? "Unlimited"
      : Number(limits?.forkSimulations || 0);

    return {
      plan,
      planLabel: getPlanLabel(plan),
      isPaidPlan: isPaidPlan(plan),
      canBuyCapacityPack: canBuyDeploymentCapacityPack(plan),
      isUnlimited,

      deploymentsUsed,
      deploymentsLimit,
      includedDeployments: Number(limits?.includedDeployments || 0),
      extraDeployments: Number(limits?.extraDeployments || 0),

      forkSimulationsUsed,
      forkSimulationsLimit,
      includedForkSimulations: Number(limits?.includedForkSimulations || 0),
      extraForkSimulations: Number(limits?.extraForkSimulations || 0),

      isDeploymentLimitReached:
        !isUnlimited &&
        deploymentsLimit > 0 &&
        deploymentsUsed >= deploymentsLimit,
    };
  }, [accountUsage]);

  const startDeploymentPackCheckout = useCallback(async () => {
    if (!isAuthed) {
      setCheckoutError("Sign in before buying a Deployment Capacity Pack.");
      return;
    }

    if (!canBuyDeploymentCapacityPack(planSummary.plan)) {
      setCheckoutError("Deployment Capacity Packs are available on the Pro plan.");
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError("");

    try {
      const res = await api.post("/billing/extra-deployment-pack-checkout");
      const transactionId = String(res?.data?.transactionId || "").trim();
      const url = String(res?.data?.url || "").trim();

      if (transactionId) {
        window.location.href = `/upgrade?_ptxn=${encodeURIComponent(
          transactionId
        )}&plan=extra_pack`;
        return;
      }

      if (!url) {
        throw new Error("Missing checkout URL.");
      }

      window.location.href = url;
    } catch (err) {
      setCheckoutError(
        safeApiError(err, "Failed to start Deployment Capacity Pack checkout."),
      );
    } finally {
      setCheckoutLoading(false);
    }
  }, [isAuthed, planSummary.plan]);

  const handleAddDeploymentPack = useCallback(() => {
    if (!isAuthed) {
      setCheckoutError("Sign in before buying a Deployment Capacity Pack.");
      return;
    }

    if (!canBuyDeploymentCapacityPack(planSummary.plan)) {
      setCheckoutError("Deployment Capacity Packs are available on the Pro plan.");
      return;
    }

    if (!acceptedBillingTerms) {
      setPendingCheckout({
        type: "extra_pack",
      });
      setTermsModalOpen(true);
      setCheckoutError("");
      return;
    }

    startDeploymentPackCheckout();
  }, [
    isAuthed,
    planSummary.plan,
    acceptedBillingTerms,
    startDeploymentPackCheckout,
  ]);

  const handleTermsModalContinue = useCallback(() => {
    if (!acceptedBillingTerms || !pendingCheckout) return;

    const checkout = pendingCheckout;

    setTermsModalOpen(false);
    setPendingCheckout(null);

    if (checkout.type === "plan") {
      startPlanCheckout(checkout.plan);
      return;
    }

    if (checkout.type === "extra_pack") {
      startDeploymentPackCheckout();
    }
  }, [
    acceptedBillingTerms,
    pendingCheckout,
    startPlanCheckout,
    startDeploymentPackCheckout,
  ]);

  const currentPlanCta = useMemo(() => {
    if (planSummary.plan === "pro") {
      if (planSummary.isDeploymentLimitReached) {
        return {
          title: "Pro deployment limit reached",
          subtitle:
            "Add a Deployment Capacity Pack: +50 deployments and +60 preflight simulations for the current billing period.",
          buttonText: "Add Deployment Capacity Pack",
          mode: "extra_pack",
          disabled: false,
        };
      }

      return {
        title: "You are already on Pro",
        subtitle:
          planSummary.extraDeployments > 0
            ? `Total limit: ${planSummary.deploymentsLimit} deployments and ${planSummary.forkSimulationsLimit} preflight simulations this billing period.`
            : "Pro access is enabled. Deployment Capacity Packs are available when you need more usage.",
        buttonText: "Current Plan",
        mode: "current",
        disabled: true,
      };
    }

    if (planSummary.plan === "starter") {
      return {
        title: "Upgrade from Starter",
        subtitle:
          "Move to Pro for higher monthly limits and access to Deployment Capacity Packs.",
        buttonText: "Upgrade to Pro",
        mode: "pro_checkout",
        disabled: false,
      };
    }

    return {
      title: "Choose your workspace plan",
      subtitle:
        "Starter and Pro include ERC20/ERC721/ERC1155 support, token operations, verification tracking, deployment history, analytics, and liquidity visibility where available.",
      buttonText: "Choose a Plan",
      mode: "choose",
      disabled: false,
    };
  }, [planSummary]);

  return (
    <div className="min-h-screen bg-[#F7F8FA] px-4 py-6 text-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <section className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-[#050713] p-6 text-white shadow-[0_24px_80px_rgba(15,23,42,0.22)] sm:p-8 lg:p-10">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#C59B45]/20 blur-3xl" />
            <div className="absolute -right-24 top-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#E6C978]/50 to-transparent" />
          </div>

          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-end">
            <div>
              <div className="inline-flex items-center rounded-full border border-[#C59B45]/30 bg-[#C59B45]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E6C978]">
                KRIONEX PLANS
              </div>

              <h1 className="mt-5 max-w-3xl text-3xl font-semibold tracking-[-0.04em] text-white sm:text-4xl lg:text-5xl">
                Upgrade your token launch operations workspace.
              </h1>

              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300 sm:text-base">
                Build with standard token templates or custom Solidity, using the same shared deployment and preflight allowances. Upgrade for higher monthly limits, token operations, verification tracking, deployment history, analytics, and liquidity visibility where available.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-slate-200">
                  Wallet-confirmed workflows
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-slate-200">
                  Platform access only
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-slate-200">
                  Gas paid by user wallet
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-slate-200">
                  No private key custody
                </div>

                <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-slate-200">
                  Standard + custom shared limits
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Current workspace
              </div>

              {isAuthed && accountUsageLoading && (
                <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm text-slate-300">
                  Loading your current plan and usage…
                </div>
              )}

              {isAuthed && accountUsageError && (
                <div className="mt-4 rounded-2xl border border-amber-300/20 bg-amber-300/10 p-4 text-sm text-amber-100">
                  {accountUsageError}
                </div>
              )}

              {isAuthed && !accountUsageLoading && !accountUsageError && (
                <div className="mt-4">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="text-sm text-slate-400">Current plan</div>
                      <div className="mt-1 text-2xl font-semibold capitalize tracking-tight text-white">
                        {planSummary.planLabel}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <div className="rounded-full border border-[#C59B45]/30 bg-[#C59B45]/10 px-3 py-1 text-xs font-semibold text-[#E6C978]">
                        {planSummary.deploymentsUsed} /{" "}
                        {planSummary.deploymentsLimit || "—"} deployments
                      </div>

                      <div className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs font-semibold text-slate-300">
                        {planSummary.forkSimulationsUsed} /{" "}
                        {planSummary.forkSimulationsLimit || "—"} preflights
                      </div>
                    </div>
                  </div>

                  {planSummary.isPaidPlan && !planSummary.isUnlimited && (
                    <div className="mt-3 text-xs leading-5 text-slate-400">
                      Included: {planSummary.includedDeployments || 0} deployments
                      {" + "}
                      {planSummary.includedForkSimulations || 0} preflights
                      {planSummary.extraDeployments > 0 || planSummary.extraForkSimulations > 0
                        ? ` · Deployment Capacity Pack: ${planSummary.extraDeployments} deployments + ${planSummary.extraForkSimulations} preflights`
                        : ""}
                    </div>
                  )}

                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#C59B45] to-[#E6C978]"
                      style={{
                        width:
                          planSummary.deploymentsLimit === "Unlimited"
                            ? "100%"
                            : `${Math.min(
                              100,
                              Math.round(
                                (Number(planSummary.deploymentsUsed || 0) /
                                  Math.max(
                                    1,
                                    Number(planSummary.deploymentsLimit || 1),
                                  )) *
                                100,
                              ),
                            )}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {!isAuthed && (
                <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm leading-6 text-slate-300">
                  Sign in from the top header to view your current plan and
                  start checkout.
                </div>
              )}
            </div>
          </div>
        </section>

        {(checkoutError ||
          checkoutState === "success" ||
          checkoutState === "starter_success" ||
          checkoutState === "pro_success" ||
          checkoutState === "extra_success" ||
          checkoutState === "cancel") && (
            <div className="space-y-3">
              {checkoutError && (
                <div className="rounded-3xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-800 shadow-sm">
                  {checkoutError}
                </div>
              )}

              {checkoutState === "success" && (
                <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-800 shadow-sm">
                  Payment completed. Syncing your plan…
                </div>
              )}
              {checkoutState === "starter_success" && (
                <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-800 shadow-sm">
                  Payment completed. Syncing your Starter plan…
                </div>
              )}

              {checkoutState === "pro_success" && (
                <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-800 shadow-sm">
                  Payment completed. Syncing your Pro plan…
                </div>
              )}

              {checkoutState === "extra_success" && (
                <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-800 shadow-sm">
                  Deployment Capacity Pack purchased. Your limit will update after Paddle confirms the payment.
                </div>
              )}

              {checkoutState === "cancel" && (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-800 shadow-sm">
                  Checkout was canceled.
                </div>
              )}
            </div>
          )}

        <section className="grid gap-5 xl:grid-cols-4">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                  Free
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Best for exploring Krionex with a small number of standard or custom deployments.
                </p>
              </div>
            </div>

            <div className="mt-6 text-4xl font-semibold tracking-tight text-slate-950">
              $0
              <span className="text-sm font-medium text-slate-500">
                {" "}
                / month
              </span>
            </div>

            <div className="mt-6 space-y-3 text-sm text-slate-700">
              {[
                "ERC20 deployments only",
                "5 total deployments per month: standard or custom",
                "5 total preflight simulations per month",
                "Custom Solidity compile/deploy included within quota",
                "Wallet-signed deployment workflow",
                "Verification tracking",
                "Deployment history",
                "Token dashboard access",
                "Basic token metadata and activity visibility",
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-7">
              <Link
                to="/"
                className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Continue on Free
              </Link>
            </div>
          </div>

          <div className="relative rounded-[2rem] border border-[#C59B45]/40 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.08)] ring-1 ring-[#E6C978]/20">
            <div className="absolute -top-3 right-5 rounded-full border border-[#E6C978]/40 bg-[#FFF8E1] px-3 py-1 text-xs font-semibold text-[#7A5A12] shadow-lg">
              Best start
            </div>

            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                Starter
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                For builders who want the full token workspace with lower monthly usage.
              </p>
            </div>

            <div className="mt-6 text-4xl font-semibold tracking-tight text-slate-950">
              $29
              <span className="text-sm font-medium text-slate-500"> / month</span>
            </div>

            <div className="mt-6 space-y-3 text-sm text-slate-700">
              {[
                "ERC20, ERC721, and ERC1155 support",
                "50 total deployments per month: standard or custom",
                "60 total preflight simulations per month",
                "Custom Solidity compile/deploy included",
                "Standard and custom deployments share the same allowance",
                "Mintable, burnable, and pausable modules where supported",
                "Wallet-signed token operations",
                "Verification tracking and deployment history",
                "Token dashboard access",
                "Analytics and liquidity visibility where available",
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#C59B45]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-6">
              {planSummary.plan === "starter" ? (
                <button
                  disabled
                  className="inline-flex w-full items-center justify-center rounded-2xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-500"
                >
                  Current Plan
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handlePlanCheckout("starter")}
                  disabled={checkoutLoading}
                  className="inline-flex w-full items-center justify-center rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {checkoutLoading ? "Redirecting..." : "Start with Starter"}
                </button>
              )}
            </div>
          </div>

          <div className="relative rounded-[2rem] border border-[#C59B45]/50 bg-[#050713] p-6 text-white shadow-[0_24px_70px_rgba(15,23,42,0.24)] ring-1 ring-[#E6C978]/20">
            <div className="absolute -top-3 right-5 rounded-full border border-[#E6C978]/40 bg-[#C59B45] px-3 py-1 text-xs font-semibold text-[#050713] shadow-lg">
              Recommended
            </div>

            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-white">
                Pro
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                For active builders who need higher monthly usage and Deployment Capacity Packs.
              </p>
            </div>

            <div className="mt-6 space-y-2">
              <div className="text-4xl font-semibold tracking-tight text-white">
                {planSummary.plan === "pro" ? "Active" : "$49"}
                {planSummary.plan !== "pro" && (
                  <span className="text-sm font-medium text-slate-400">
                    {" "}
                    / month
                  </span>
                )}
              </div>
              <span className="block text-sm leading-6 text-slate-300">
                150 total deployments and 180 total preflight simulations per month across standard and custom workflows, plus Deployment Capacity Packs when you need more usage.
              </span>
            </div>

            <div className="mt-6 space-y-3 text-sm text-slate-200">
              {[
                "ERC20, ERC721, and ERC1155 support",
                "150 total deployments per month: standard or custom",
                "180 total preflight simulations per month",
                "Custom Solidity compile/deploy included within quota",
                "Standard and custom deployments share the same allowance",
                "Same token launch, management, and monitoring features as Starter",
                "Mintable, burnable, and pausable modules where supported",
                "Wallet-signed token operations",
                "Verification tracking and deployment history",
                "Deployment Capacity Packs available",
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#E6C978]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.06] p-4 text-sm">
              <div className="font-semibold text-white">
                Built for higher usage
              </div>
              <div className="mt-1 leading-6 text-slate-300">
                Choose Pro when you need more deployments, more preflights, and access to Deployment Capacity Packs.
              </div>
            </div>


            <div className="mt-5 flex flex-wrap gap-3">
              {planSummary.plan === "pro" && currentPlanCta.disabled ? (
                <>
                  <button
                    disabled
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950 opacity-70"
                  >
                    {currentPlanCta.buttonText}
                  </button>

                  {planSummary.plan === "pro" && (
                    <button
                      type="button"
                      onClick={handleAddDeploymentPack}
                      disabled={checkoutLoading}
                      className="inline-flex w-full items-center justify-center rounded-2xl border border-[#E6C978]/40 bg-white/[0.06] px-4 py-3 text-sm font-semibold text-[#E6C978] transition hover:bg-white/[0.10] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {checkoutLoading ? "Redirecting..." : "Add Deployment Capacity Pack"}
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={
                    currentPlanCta.mode === "extra_pack"
                      ? handleAddDeploymentPack
                      : () => handlePlanCheckout("pro")
                  }
                  disabled={checkoutLoading}
                  className="inline-flex w-full items-center justify-center rounded-2xl bg-[#E6C978] px-4 py-3 text-sm font-semibold text-[#050713] shadow-[0_16px_45px_rgba(197,155,69,0.28)] transition hover:bg-[#f1d989] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {checkoutLoading ? "Redirecting..." : currentPlanCta.mode === "choose" ? "Upgrade to Pro" : currentPlanCta.buttonText}
                </button>
              )}

              <Link
                to="/help-center"
                className="inline-flex w-full items-center justify-center rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/[0.10]"
              >
                Contact Support
              </Link>
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                Team
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                For agencies, teams, and high-volume workflows that need custom limits, support, and rollout guidance.
              </p>
            </div>

            <div className="mt-6 space-y-2">
              <div className="text-3xl font-semibold tracking-tight text-slate-950">
                Contact Krionex
              </div>
              <span className="block text-sm leading-6 text-slate-500">
                Custom workspace access and operational limits.
              </span>
            </div>

            <div className="mt-6 space-y-3 text-sm text-slate-700">
              {[
                "Custom deployment limits",
                "Custom preflight simulation limits",
                "Agency or team-oriented workspace support",
                "ERC20, ERC721, and ERC1155 support",
                "Token launch and operations workflows",
                "Priority support",
                "Custom rollout guidance",
                "Supported networks included",
                "Verification tracking included",
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <div className="font-semibold text-slate-950">
                Need team access?
              </div>
              <div className="mt-1 leading-6 text-slate-600">
                Contact Krionex to discuss usage limits, support, and rollout
                needs.
              </div>
            </div>

            <div className="mt-5">
              <Link
                to="/contact"
                className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Contact Krionex
              </Link>
            </div>
          </div>
        </section>

        {termsModalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
            <div className="w-full max-w-lg rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_28px_90px_rgba(15,23,42,0.28)]">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8A6A1F]">
                Billing confirmation
              </div>

              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                Accept terms to continue
              </h2>

              <p className="mt-3 text-sm leading-6 text-slate-600">
                Krionex subscriptions cover platform access, deployment/preflight
                allowances, token operations, verification tracking, dashboards, and
                custom Solidity workflows where available. Blockchain gas, wallet fees,
                and network fees are separate and paid directly from your wallet.
              </p>

              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                Standard and custom deployments share the same monthly deployment
                allowance. Standard and custom preflight simulations share the same
                monthly preflight allowance.
              </div>

              <label className="mt-5 flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-700">
                <input
                  type="checkbox"
                  checked={acceptedBillingTerms}
                  onChange={(e) => {
                    setAcceptedBillingTerms(e.target.checked);
                    if (e.target.checked) setCheckoutError("");
                  }}
                  className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#C59B45]"
                />
                <span>
                  I agree to the{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-[#8A6A1F] hover:text-slate-950"
                  >
                    Terms of Service
                  </Link>
                  ,{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-[#8A6A1F] hover:text-slate-950"
                  >
                    Privacy Policy
                  </Link>
                  , and Krionex billing, cancellation, and refund terms.
                </span>
              </label>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setTermsModalOpen(false);
                    setPendingCheckout(null);
                  }}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleTermsModalContinue}
                  disabled={!acceptedBillingTerms || checkoutLoading}
                  className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {checkoutLoading ? "Redirecting." : "Accept and Continue"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="rounded-3xl border border-slate-200 bg-white px-5 py-4 text-sm leading-6 text-slate-700 shadow-sm">
          When you start checkout, Krionex will ask you to accept the{" "}
          <Link
            to="/terms"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-[#8A6A1F] hover:text-slate-950"
          >
            Terms of Service
          </Link>
          ,{" "}
          <Link
            to="/privacy"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-[#8A6A1F] hover:text-slate-950"
          >
            Privacy Policy
          </Link>
          , and billing, cancellation, and refund terms before redirecting to payment.
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white px-5 py-4 text-xs leading-6 text-slate-500 shadow-sm">
          Subscription fees are for Krionex platform access only. Blockchain gas
          fees, wallet fees, and network fees are separate and paid directly by
          users through their own wallets.
        </div>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
          <h2 className="text-lg font-semibold tracking-tight text-slate-950">
            Plan rules
          </h2>

          <div className="mt-4 grid gap-3 text-sm leading-6 text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="font-semibold text-slate-950">
                Starter and Pro include the same core features.
              </div>
              <div className="mt-1">
                Starter has lower monthly usage. Pro has higher monthly usage and can add Deployment Capacity Packs.
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="font-semibold text-slate-950">
                Gas is separate.
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
  <div className="font-semibold text-slate-950">
    Standard and custom share one allowance.
  </div>
  <div className="mt-1">
    A deployment credit can be used for a standard template deployment or a custom Solidity deployment. A preflight credit can be used for either standard or custom preflight simulation.
  </div>
</div>
              <div className="mt-1">
                Krionex plan limits cover platform access. Blockchain gas, wallet, and network fees are paid from your wallet.
              </div>
            </div>
          </div>
        </section>


        <section className="flex flex-col gap-4 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)] sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-slate-950">
              Need a custom plan?
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
              Reach out if you need higher limits, team-oriented access, or
              custom rollout support for advanced token operations.
            </p>
          </div>

          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Contact Krionex
          </Link>
        </section>
      </div>
    </div>
  );
}
